import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Ticket, Trash2, Power, ShieldAlert, Check, Store, Ban, RotateCcw, CreditCard, Loader2 } from "lucide-react";

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState("");
  const [percentOff, setPercentOff] = useState("100");
  const [duration, setDuration] = useState("forever");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [saving, setSaving] = useState(false);
  const [modQueue, setModQueue] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [reasonInputs, setReasonInputs] = useState({});
  const [billingSetupBusy, setBillingSetupBusy] = useState(false);
  const [billingSetupResult, setBillingSetupResult] = useState(null);

  const load = () => api.get("/admin/promo-codes").then((r) => setCodes(r.data)).catch(() => setCodes([]));
  const loadModQueue = () => api.get("/admin/kf/moderation-queue").then((r) => setModQueue(r.data)).catch(() => setModQueue([]));
  const loadMerchants = () => api.get("/admin/bazaar/merchants").then((r) => setMerchants(r.data)).catch(() => setMerchants([]));

  useEffect(() => {
    if (user?.is_platform_admin) {
      load();
      loadModQueue();
      loadMerchants();
    }
  }, [user]);

  if (user && !user.is_platform_admin) {
    return (
      <div className="min-h-screen bg-[#151613] text-[#f1e0c5] flex items-center justify-center" data-testid="admin-page-forbidden">
        <p className="text-sm text-[#f1e0c5]/50">You don't have access to this page.</p>
      </div>
    );
  }

  const createCode = async () => {
    if (!code.trim()) return toast.error("Give the code a name, e.g. FRIENDS100");
    const pct = parseInt(percentOff, 10);
    if (!pct || pct < 1 || pct > 100) return toast.error("Percent off must be between 1 and 100");
    setSaving(true);
    try {
      await api.post("/admin/promo-codes", {
        code: code.trim(),
        percent_off: pct,
        duration,
        max_redemptions: maxRedemptions.trim() ? parseInt(maxRedemptions, 10) : null,
      });
      setCode("");
      setPercentOff("100");
      setMaxRedemptions("");
      toast.success("Promo code created");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create promo code");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/promo-codes/${c.id}`, { active: !c.active });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update promo code");
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/promo-codes/${c.id}`);
      toast.success("Promo code deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete promo code");
    }
  };

  const dismissReports = async (postId) => {
    try {
      await api.post(`/admin/kf/moderation-queue/${postId}/dismiss`);
      setModQueue((prev) => prev.filter((p) => p.id !== postId));
      toast.success("Dismissed — post stays up");
    } catch {
      toast.error("Couldn't dismiss those reports");
    }
  };

  const removeFlaggedPost = async (postId) => {
    if (!window.confirm("Permanently delete this Knights Fee post?")) return;
    try {
      await api.delete(`/admin/kf/moderation-queue/${postId}`);
      setModQueue((prev) => prev.filter((p) => p.id !== postId));
      toast.success("Post deleted");
    } catch {
      toast.error("Couldn't delete that post");
    }
  };

  const moderateMerchant = async (merchantId, action) => {
    const reason = reasonInputs[merchantId] || "";
    if (action !== "reinstate" && !reason.trim()) return toast.error("Give a reason for the record");
    try {
      await api.post(`/admin/bazaar/merchants/${merchantId}/${action}`, action === "reinstate" ? {} : { reason });
      toast.success(action === "suspend" ? "Merchant suspended" : action === "ban" ? "Merchant banned" : "Merchant reinstated");
      loadMerchants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update merchant status");
    }
  };

  const runBillingSetup = async () => {
    setBillingSetupBusy(true);
    setBillingSetupResult(null);
    try {
      const { data } = await api.post("/admin/billing/setup-catalog");
      setBillingSetupResult(data.results);
      toast.success("Stripe billing catalog & portal set up — Manage Subscription should now show only Call to Arms");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed — check backend logs");
    } finally {
      setBillingSetupBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5]" data-testid="admin-page">
      <nav className="flex items-center px-6 md:px-12 py-6 border-b border-white/5">
        <button data-testid="admin-back-button" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-[#f1e0c5]/60 hover:text-[#D4AF37]">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>
      </nav>
      <div className="px-6 md:px-12 py-12 max-w-3xl mx-auto">
        <h1 className="font-display text-4xl mb-2 flex items-center gap-3" data-testid="admin-billing-setup-heading"><CreditCard className="w-8 h-8 text-[#D4AF37]" /> Stripe Billing Setup</h1>
        <p className="text-sm text-[#f1e0c5]/50 mb-4">
          One-time (safe to re-run anytime) setup that creates the GM Plan product/prices and a Call-to-Arms-only "Manage Subscription" portal —
          separate from any other app sharing this Stripe account. Run this whenever Call to Arms's Stripe setup needs to be (re)applied.
        </p>
        <div className="p-6 rounded-2xl bg-black/40 border border-white/10 mb-16 space-y-3" data-testid="admin-billing-setup-panel">
          <Button data-testid="admin-run-billing-setup-button" onClick={runBillingSetup} disabled={billingSetupBusy} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19b2e] gap-2">
            {billingSetupBusy && <Loader2 className="w-4 h-4 animate-spin" />} {billingSetupBusy ? "Setting up…" : "Run Stripe Billing Setup"}
          </Button>
          {billingSetupResult && (
            <div data-testid="admin-billing-setup-result" className="text-xs text-[#f1e0c5]/60 space-y-1 pt-2">
              {billingSetupResult.map((r) => (
                <p key={r.product_id}>✓ {r.product} — product <span className="font-mono">{r.product_id}</span>, portal config <span className="font-mono">{r.portal_configuration_id}</span></p>
              ))}
            </div>
          )}
        </div>

        <h1 className="font-display text-4xl mb-2 flex items-center gap-3"><Ticket className="w-8 h-8 text-[#D4AF37]" /> Promo Codes</h1>
        <p className="text-sm text-[#f1e0c5]/50 mb-8">Give someone a percent off the GM plan — subscription or one-time pass.</p>

        <div className="p-6 rounded-2xl bg-black/40 border border-white/10 mb-8 space-y-3" data-testid="admin-create-promo-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-[#f1e0c5]/50">Code</label>
              <Input data-testid="admin-promo-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="FRIENDS100" className="bg-black/30 border-white/10 uppercase" />
            </div>
            <div>
              <label className="text-xs text-[#f1e0c5]/50">Percent Off</label>
              <Input data-testid="admin-promo-percent-input" type="number" min={1} max={100} value={percentOff} onChange={(e) => setPercentOff(e.target.value)} className="bg-black/30 border-white/10" />
            </div>
            <div>
              <label className="text-xs text-[#f1e0c5]/50">Duration</label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger data-testid="admin-promo-duration-select" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="forever">Forever (whole subscription)</SelectItem>
                  <SelectItem value="once">Once (first month/payment only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#f1e0c5]/50">Max Uses</label>
              <Input data-testid="admin-promo-max-redemptions-input" type="number" min={1} value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} placeholder="Unlimited" className="bg-black/30 border-white/10" />
            </div>
          </div>
          <Button data-testid="admin-create-promo-button" onClick={createCode} disabled={saving} className="rounded-full bg-[#D90429] hover:bg-[#b8031f]">
            {saving ? "Creating…" : "Create Promo Code"}
          </Button>
        </div>

        <div className="space-y-2">
          {codes.map((c) => (
            <div key={c.id} data-testid={`admin-promo-row-${c.code}`} className="flex items-center justify-between bg-black/30 border border-white/10 rounded-xl px-4 py-3">
              <div>
                <p className="font-display text-lg tracking-wide">{c.code} <span className="text-sm text-[#D4AF37]">{c.percent_off}% off</span> <span className="text-xs text-[#f1e0c5]/40 capitalize">· {c.duration}</span></p>
                <p className="text-xs text-[#f1e0c5]/40">
                  Used {c.redemption_count}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : " (unlimited)"} · {c.active ? "Active" : "Disabled"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid={`admin-promo-toggle-${c.code}`} size="sm" variant="secondary" className="rounded-full gap-1.5 text-xs" onClick={() => toggleActive(c)}>
                  <Power className="w-3.5 h-3.5" /> {c.active ? "Disable" : "Enable"}
                </Button>
                <button data-testid={`admin-promo-delete-${c.code}`} onClick={() => remove(c)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {codes.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No promo codes yet.</p>}
        </div>

        <h1 className="font-display text-4xl mb-2 mt-16 flex items-center gap-3" data-testid="admin-moderation-heading"><ShieldAlert className="w-8 h-8 text-[#D90429]" /> Knights Fee Moderation</h1>
        <p className="text-sm text-[#f1e0c5]/50 mb-8">Posts flagged by AI moderation ("mature"/"needs_review") or reported by a Knight — dismiss the reports to keep it up, or delete it.</p>
        <div className="space-y-3" data-testid="admin-moderation-queue">
          {modQueue.length === 0 ? (
            <p className="text-xs text-[#f1e0c5]/30 italic">Nothing needs your attention right now.</p>
          ) : (
            modQueue.map((p) => (
              <div key={p.id} data-testid={`admin-moderation-row-${p.id}`} className="bg-black/30 border border-white/10 rounded-xl px-4 py-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold">{p.author.name}</p>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider">
                    <span className={`px-2 py-0.5 rounded-full ${p.content_flag === "mature" ? "bg-[#D90429]/20 text-[#D90429]" : p.content_flag === "needs_review" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/10 text-[#f1e0c5]/50"}`}>{p.content_flag}</span>
                    {p.report_count > 0 && <span className="px-2 py-0.5 rounded-full bg-white/10 text-[#f1e0c5]/60">{p.report_count} report{p.report_count > 1 ? "s" : ""}</span>}
                  </div>
                </div>
                {p.content && <p className="text-sm text-[#f1e0c5]/70 whitespace-pre-wrap">{p.content}</p>}
                {p.image_url && <img src={p.image_url} alt="" className="rounded-lg max-h-48 border border-white/10" />}
                {p.moderation_reason && <p className="text-xs text-[#f1e0c5]/40 italic">AI reason: {p.moderation_reason}</p>}
                <div className="flex items-center gap-2 pt-1">
                  <Button data-testid={`admin-moderation-dismiss-${p.id}`} size="sm" variant="secondary" className="rounded-full gap-1.5 text-xs" onClick={() => dismissReports(p.id)}>
                    <Check className="w-3.5 h-3.5" /> Dismiss
                  </Button>
                  <button data-testid={`admin-moderation-delete-${p.id}`} onClick={() => removeFlaggedPost(p.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))
          )}
        </div>

        <h1 className="font-display text-4xl mb-2 mt-16 flex items-center gap-3" data-testid="admin-merchants-heading"><Store className="w-8 h-8 text-[#D4AF37]" /> Bazaar Merchants</h1>
        <p className="text-sm text-[#f1e0c5]/50 mb-8">Suspend hides a seller's listings while they resolve an issue; Ban is permanent and blocks re-onboarding. Both require a reason for the record.</p>
        <div className="space-y-3" data-testid="admin-merchants-list">
          {merchants.length === 0 ? (
            <p className="text-xs text-[#f1e0c5]/30 italic">No merchants yet.</p>
          ) : (
            merchants.map((m) => (
              <div key={m.id} data-testid={`admin-merchant-row-${m.id}`} className="bg-black/30 border border-white/10 rounded-xl px-4 py-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">{m.merchant_profile?.display_name || m.name} <span className="text-xs text-[#f1e0c5]/40">({m.email})</span></p>
                    <p className="text-xs text-[#f1e0c5]/40">{m.active_listing_count} active listing{m.active_listing_count === 1 ? "" : "s"} · Real name: {m.merchant_profile?.real_name || "—"}</p>
                  </div>
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${m.merchant_status === "banned" ? "bg-[#D90429]/20 text-[#D90429]" : m.merchant_status === "suspended" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/10 text-[#f1e0c5]/50"}`}>{m.merchant_status}</span>
                </div>
                {m.merchant_profile?.bio && <p className="text-xs text-[#f1e0c5]/50 italic">"{m.merchant_profile.bio}"</p>}
                {m.merchant_status === "active" ? (
                  <div className="flex items-center gap-2">
                    <Input data-testid={`admin-merchant-reason-${m.id}`} placeholder="Reason (required)" value={reasonInputs[m.id] || ""} onChange={(e) => setReasonInputs((p) => ({ ...p, [m.id]: e.target.value }))} className="bg-black/30 border-white/10 h-8 text-xs flex-1" />
                    <Button data-testid={`admin-merchant-suspend-${m.id}`} size="sm" variant="secondary" className="rounded-full gap-1.5 text-xs h-8" onClick={() => moderateMerchant(m.id, "suspend")}>
                      <ShieldAlert className="w-3.5 h-3.5" /> Suspend
                    </Button>
                    <Button data-testid={`admin-merchant-ban-${m.id}`} size="sm" className="rounded-full gap-1.5 text-xs h-8 bg-[#D90429] hover:bg-[#b8031f]" onClick={() => moderateMerchant(m.id, "ban")}>
                      <Ban className="w-3.5 h-3.5" /> Ban
                    </Button>
                  </div>
                ) : (
                  <Button data-testid={`admin-merchant-reinstate-${m.id}`} size="sm" variant="secondary" className="rounded-full gap-1.5 text-xs h-8" onClick={() => moderateMerchant(m.id, "reinstate")}>
                    <RotateCcw className="w-3.5 h-3.5" /> Reinstate
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
